import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import MiscUtils from 'services/MiscUtils';
import Icon from 'components/Utils/NewIcon';
import AppointmentUtils, { APPOINTMENT_STATUS } from 'services/AppointmentUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import JoinVideoButton from 'components/HealthRecords/Appointments/JoinVideoButton';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { PhysicianImage } from 'components/Common/UserImage';
import PhysicianUtils from 'services/PhysicianUtils';
import InfoCard from 'components/Utils/InfoCard';
import moment from 'moment-timezone';
import DefaultConfirmationModal from 'components/Utils/DefaultConfirmationScreen';
import RateAppointment from 'components/HealthRecords/Appointments/RateAppointment';
import bgSquares from 'assets/img/bg-squares.webp';
import GoogleMap from 'components/Utils/GoogleMap';
import ClinicUtils from 'services/ClinicUtils';
import DefaultPatientModal from 'components/Utils/DefaultPatientModal';
import SlotSchedule from 'scenes/HomePatient/AppointmentBooking/DateTime/SlotSchedule';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { Device } from '../../../../services/Native/Device';
import { store } from 'careand-redux/store';
import { apiRequestConfig } from 'careand-redux/api/ApiThunks';

const PatientAppointmentDetails = ({ appointment, showActions = true, loading, ...props }) => {
  const dispatch = useAppDispatch();
  const patient = useAppSelector((state) => state.patients.byId[appointment.patient_id]);
  const [loadingICS, setLoadingICS] = useState(false);

  const physician = useAppSelector((state) => state.physicians.byId[appointment.physician_id]);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const options = useAppSelector((state) => state.appointments.options);

  useEffect(() => {
    dispatch(
      api.Appointments.getBookingFlowOptions({
        patientId: appointment.patient_id,
      }),
    );
  }, [dispatch, appointment.patient_id]);

  const showMapButton =
    AppointmentUtils.isClinicAppointment(appointment) && AppointmentUtils.isActive(appointment);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isActiveTelemedicine, setActiveTelemedicine] = useState(false);

  useEffect(() => {
    if (appointment.id && AppointmentUtils.isTelemedicineAppointment(appointment)) {
      dispatch(api.Video.isActiveVideoAppointment({ appointmentId: appointment.id })).then(
        (result) => {
          setActiveTelemedicine(result?.value);
        },
      );
    }
  }, [dispatch, appointment]);

  async function downloadICS() {
    setLoadingICS(true);
    try {
      if (Device.isMobile()) {
        const token = store.getState().authentication.token;
        if (!token) {
          MiscUtils.showToastrError(
            'Add to Calendar',
            'Your session has expired. Please sign in again.',
          );
          return;
        }
        const request = apiRequestConfig.Appointments.getICS({ id: appointment.id });
        await Device.openFile(token, request, 'Appointment.ics', { timeout: 15_000 });
        return;
      }

      const result = await dispatch(api.Appointments.getICS({ id: appointment.id }));
      if (!result) return;

      const blob = new Blob([result], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = 'Appointment.ics';
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      MiscUtils.showToastrError(
        'Add to Calendar',
        "Couldn't open the calendar file. Please try again.",
      );
    } finally {
      setLoadingICS(false);
    }
  }

  async function cancelAppointment() {
    setCancelling(true);
    await dispatch(
      api.Appointments.updateAppointmentStatus({
        id: appointment.id,
        status: 'cancelled',
      }),
      {
        title: 'Cancel Appointment',
        successText: 'Appointment cancelled successfully!',
      },
    );
    setCancelling(false);
    setConfirmationDialogOpen(false);
  }

  const concern = useMemo(() => {
    if (appointment.concern_text) {
      return appointment.concern_text;
    }
    return options
      .map(({ concerns = [] }) => concerns.find(({ id }) => id === appointment.concern_id))
      .find((concern) => !!concern)?.text;
  }, [appointment, options]);

  const appointmentClinic = useMemo(() => {
    return clinics.find(({ id }) => id === appointment.clinic_id);
  }, [appointment, clinics]);

  const address = useMemo(() => {
    if (appointment.address?.line1) return appointment.address;
    if (AppointmentUtils.isClinicAppointment(appointment)) {
      return appointmentClinic?.address;
    }
  }, [appointment, appointmentClinic]);

  const inTheFuture = moment.unix(appointment.date).isAfter(moment());

  const onClickShowInMap = () => {
    MiscUtils.openExternalLink(appointmentClinic.google_maps_url);
  };

  const lateWarning = `Your appointment will be cancelled automatically if you arrive more than ${appointment.cancel_cutoff} minutes late`;

  const isPastStartDate = moment().unix() > appointment.date;

  const allowReschedule = useMemo(() => {
    const cutoffHours = appointment.fee ? -4 : -1;
    const outsideCutoffHours =
      moment().unix() < moment.unix(appointment.date).add(cutoffHours, 'hour').unix();
    const isScheduled = appointment.status === APPOINTMENT_STATUS.scheduled;
    return !isPastStartDate && outsideCutoffHours && isScheduled;
  }, [appointment, isPastStartDate]);

  return (
    <div className="patient-appointment-details" key={appointment.id}>
      <div className="physician-info">
        <div className="desktop-only">
          <img src={bgSquares} className="bg-squares" />
        </div>
        <PhysicianImage user={physician} size={124} />
        <span className="name">{PhysicianUtils.getName(physician)}</span>
        <span className="type">{AppointmentUtils.getAppointmentDescription(appointment)}</span>
        <span className="type">{appointment.status}</span>
      </div>
      <div className="appointment-info">
        <InfoCard title="Date & Time" className="date-time">
          <span className="date">
            <Icon src="calendar" />
            {CalendarUtils.format(appointment.date, 'DD MMMM YYYY')}
          </span>
          <span className="time">
            <Icon src="clock" />
            {CalendarUtils.format(appointment.date, 'h:mm A')}
          </span>
        </InfoCard>
        {AppointmentUtils.isActive(appointment) && appointment.cancel_cutoff && (
          <div className="mobile-only">
            <span className="warning">{lateWarning}</span>
          </div>
        )}
        {concern && <InfoCard title="Concern">{concern}</InfoCard>}
        {appointment.complaints && (
          <InfoCard className="reason" title="Reason">
            {appointment.complaints}
          </InfoCard>
        )}
        {AppointmentUtils.isHomeAppointment(appointment) && appointment.notes && (
          <InfoCard title="Notes">
            <pre>{appointment.notes}</pre>
          </InfoCard>
        )}
        {address && (
          <InfoCard title="Address">
            {showMapButton ? <span className="clinic-name">{appointmentClinic.name}</span> : null}
            {MiscUtils.getFullAddress(address)}
            {showMapButton && (
              <>
                <GoogleMap
                  center={ClinicUtils.getGoogleLatLng(appointmentClinic)}
                  latLngs={[ClinicUtils.getGoogleLatLng(appointmentClinic)]}
                  zoom={14}
                />
                <div className="map-button">
                  <DefaultButton
                    id="show-map-button"
                    name="View Directions"
                    className="light"
                    icon="location-dot"
                    onClick={onClickShowInMap}
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </InfoCard>
        )}
        {props.children}
      </div>
      {AppointmentUtils.isActive(appointment) && appointment.cancel_cutoff && (
        <div className="desktop-only">
          <span className="warning">{lateWarning}</span>
        </div>
      )}
      {inTheFuture && AppointmentUtils.isActive(appointment) ? (
        <div className="time-until">
          <Icon src="clock" width="15px" />
          <span>Your appointment is {CalendarUtils.fromNow(appointment.date)}</span>
        </div>
      ) : null}
      {showActions && (
        <>
          <div className="buttons">
            {isActiveTelemedicine && (
              <div className="join-button">
                <JoinVideoButton appointment={appointment} />
              </div>
            )}
            {AppointmentUtils.isActive(appointment) && (
              <>
                <div className="calendar-button">
                  <DefaultButton
                    id="calendar-button"
                    name="Add to Calendar"
                    icon="calendar"
                    className="light"
                    onClick={downloadICS}
                    loading={loadingICS}
                    style={{ width: '280px' }}
                  />
                </div>
                <div className="row">
                  {allowReschedule && (
                    <div className="calendar-button">
                      <DefaultButton
                        id="reschedule-button"
                        name="Reschedule Appointment"
                        icon="calendar-clock"
                        onClick={() => setRescheduleDialogOpen(true)}
                        style={{ width: '280px' }}
                      />
                    </div>
                  )}
                  <div className="cancel-button">
                    <DefaultButton
                      id="cancel-button"
                      name="Cancel Appointment"
                      icon="calendar-xmark"
                      className="red"
                      onClick={() => setConfirmationDialogOpen(true)}
                      style={{ width: '280px' }}
                      disabled={isPastStartDate}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DefaultConfirmationModal
            title="Cancel Appointment"
            open={confirmationDialogOpen}
            icon="calendar-xmark"
            text="Are you sure you want to cancel your appointment?"
            onConfirm={cancelAppointment}
            confirmLabel="Yes - Cancel the appointment"
            cancelLabel="No - Keep the appointment"
            onCancel={() => setConfirmationDialogOpen(false)}
            loadingState={cancelling}
          />
          <DefaultPatientModal
            title="Reschedule Appointment"
            className="reschedule-appointment-modal"
            fullHeight={false}
            wrap={true}
            padding={true}
            scroll={true}
            content={
              <RescheduleAppointment
                patient={patient}
                appointment={appointment}
                onSuccess={() => setRescheduleDialogOpen(false)}
              />
            }
            onRequestClose={() => setRescheduleDialogOpen(false)}
            isOpen={rescheduleDialogOpen}
          />
          <RateAppointment appointment={appointment} patient={patient} showInfo={false} />
        </>
      )}
    </div>
  );
};

const RescheduleAppointment = ({ patient, appointment, onSuccess }) => {
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(api.Appointments.rescheduleAppointment);

  async function onSelect(slot) {
    if (slot) {
      let newAppointment = await dispatch(
        api.Appointments.rescheduleAppointment({
          userId: patient.id,
          rescheduleAppointment: {
            physician_id: slot.physicians[0],
            appointment_id: appointment.id,
            date: slot.start,
            date_end: slot.end,
          },
        }),
        { errorTitle: 'Appointment Reschedule' },
      );
      if (newAppointment) {
        return onSuccess(newAppointment);
      }
    }
  }

  if (isLoading) {
    return <ProgressMessage message="Rescheduling your appointment..." />;
  }

  return (
    patient &&
    appointment && <SlotSchedule patient={patient} appointment={appointment} onSelect={onSelect} />
  );
};

export default PatientAppointmentDetails;

PatientAppointmentDetails.propTypes = {
  appointment: PropTypes.object.isRequired,
  showActions: PropTypes.bool,
};
